import { create } from 'zustand';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://vclass.vskul.com/api/v1';

export const useDashboardStore = create((set, get) => ({
  stats: null,
  users: [],
  rooms: [],
  recordings: [],
  systemHealth: null,
  isLoading: false,
  error: null,

  getStats: async (token) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      set({ stats: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: 'Failed to load stats', isLoading: false });
      throw error;
    }
  },

  getUsers: async (token, page = 1, limit = 20) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get(`${API_URL}/admin/users?page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      set({ users: response.data.users, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: 'Failed to load users', isLoading: false });
      throw error;
    }
  },

  getRooms: async (token, page = 1, limit = 20) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get(`${API_URL}/admin/rooms?page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      set({ rooms: response.data.rooms, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: 'Failed to load rooms', isLoading: false });
      throw error;
    }
  },

  getRecordings: async (token, page = 1, limit = 20) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get(`${API_URL}/admin/recordings?page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      set({ recordings: response.data.recordings, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: 'Failed to load recordings', isLoading: false });
      throw error;
    }
  },

  getSystemHealth: async (token) => {
    try {
      const response = await axios.get(`${API_URL}/admin/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      set({ systemHealth: response.data });
      return response.data;
    } catch (error) {
      console.error('Health check error:', error);
    }
  },

  deleteUser: async (token, userId) => {
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      set((state) => ({
        users: state.users.filter((u) => u.user_id !== userId),
      }));

      return true;
    } catch (error) {
      set({ error: 'Failed to delete user' });
      throw error;
    }
  },

  suspendUser: async (token, userId) => {
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/suspend`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      set((state) => ({
        users: state.users.map((u) =>
          u.user_id === userId ? { ...u, is_suspended: true } : u
        ),
      }));

      return true;
    } catch (error) {
      set({ error: 'Failed to suspend user' });
      throw error;
    }
  },

  deleteRoom: async (token, roomId) => {
    try {
      await axios.delete(`${API_URL}/admin/rooms/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      set((state) => ({
        rooms: state.rooms.filter((r) => r.live_class_id !== roomId),
      }));

      return true;
    } catch (error) {
      set({ error: 'Failed to delete room' });
      throw error;
    }
  },

  deleteRecording: async (token, recordingId) => {
    try {
      await axios.delete(`${API_URL}/admin/recordings/${recordingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      set((state) => ({
        recordings: state.recordings.filter((r) => r.id !== recordingId),
      }));

      return true;
    } catch (error) {
      set({ error: 'Failed to delete recording' });
      throw error;
    }
  },

  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
}));
